
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GDPRProvider } from "@/contexts/GDPRContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PendingSupervisionDialog } from "@/components/supervision/PendingSupervisionDialog";
import { ConsentBanner } from "@/components/gdpr/ConsentBanner";
import { SubscriptionGuard } from "@/components/subscription/SubscriptionGuard";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Putting from "./pages/Putting";
import Results from "./pages/Results";
import Training from "./pages/Training";
import Videos from "./pages/Videos";
import Profile from "./pages/Profile";
import Approaches from "./pages/Approaches";
import NotFound from "./pages/NotFound";
import Level1Training from "./pages/Level1Training";
import Level2Training from "./pages/Level2Training";
import Level3Training from "./pages/Level3Training";
import PuttingTraining from "./pages/PuttingTraining";
import ChippingTraining from "./pages/ChippingTraining";
import WedgingTraining from "./pages/WedgingTraining";
import BunkerTraining from "./pages/BunkerTraining";
import DriverTraining from "./pages/DriverTraining";
import SwingTraining from "./pages/SwingTraining";
import TrainingResults from "./pages/TrainingResults";
import BallDistance from "./pages/BallDistance";
import Help from "./pages/Help";
import GDPR from "./pages/GDPR";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LegalNotice from "./pages/LegalNotice";
import CoachDashboard from "./pages/CoachDashboard";
import SupervisionManagement from "./pages/SupervisionManagement";
import AIAnalysis from "./pages/AIAnalysis";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Subscription from "./pages/Subscription";
import CourseStats from "./pages/CourseStats";
import Presentation from "./pages/Presentation";

const queryClient = new QueryClient();

// Component to initialize hooks and dialogs that need Router context
const AppInitializer = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      {children}
      <PendingSupervisionDialog />
    </>
  );
};

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <GDPRProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ConsentBanner />
          <BrowserRouter>
            <AuthProvider>
              <AppInitializer>
                <SubscriptionGuard>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/putting" element={<Putting />} />
                    <Route path="/results" element={<Results />} />
                    <Route path="/training" element={<Training />} />
                    <Route path="/videos" element={<Videos />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/approaches" element={<Approaches />} />
                    <Route path="/training/level1" element={<Level1Training />} />
                    <Route path="/training/level2" element={<Level2Training />} />
                    <Route path="/training/level3" element={<Level3Training />} />
                    <Route path="/training/putting" element={<PuttingTraining />} />
                    <Route path="/training/chipping" element={<ChippingTraining />} />
                    <Route path="/training/wedging" element={<WedgingTraining />} />
                    <Route path="/training/bunker" element={<BunkerTraining />} />
                    <Route path="/training/driver" element={<DriverTraining />} />
                    <Route path="/training/swing" element={<SwingTraining />} />
                    <Route path="/training-results" element={<TrainingResults />} />
                    <Route path="/ball-distance" element={<BallDistance />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/gdpr" element={<GDPR />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/legal" element={<LegalNotice />} />
                    <Route path="/coach-dashboard" element={<CoachDashboard />} />
                    <Route path="/supervision-management" element={<SupervisionManagement />} />
                    <Route path="/ai-analysis" element={<AIAnalysis />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/subscription" element={<Subscription />} />
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/course-stats" element={<CourseStats />} />
                    <Route path="/presentation" element={<Presentation />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SubscriptionGuard>
              </AppInitializer>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </GDPRProvider>
    </QueryClientProvider>
  );
}

export default App;
